import { matchesEmployee } from './employeeSearch'

/**
 * The detailed-out report (docs/product/reports.md): each detail that touches
 * the date range, with the employee, the type and destination, the dates, its
 * status today, and what it takes out of available manpower. Pure, so the
 * screen and the exports read one result.
 */
export type DetailStatus = 'active' | 'upcoming' | 'ended'

export const DETAIL_STATUS_LABEL: Record<DetailStatus, string> = {
  active: 'Active',
  upcoming: 'Upcoming',
  ended: 'Ended',
}

export type DetailRecord = {
  employeeId: string
  identifier: string
  employee: string
  detailType: string
  team: string
  teamId: string
  destination: string
  startDate: string
  endDate: string
}

export type DetailedOutRow = {
  key: string
  employeeId: string
  identifier: string
  employee: string
  detailType: string
  team: string
  teamId: string
  supervisorId: string | null
  destination: string
  startDate: string
  endDate: string
  status: DetailStatus
  // Calendar days of the detail inside the report's date range.
  daysInRange: number
  effect: string
}

export type DetailedOutFilters = {
  teamId?: string
  supervisorId?: string
  status?: DetailStatus
  search?: string
}

function utcDay(iso: string): number {
  const [year, month, day] = iso.split('-').map(Number)
  return Date.UTC(year, month - 1, day) / 86_400_000
}

export function overlapDays(startA: string, endA: string, startB: string, endB: string): number {
  const start = Math.max(utcDay(startA), utcDay(startB))
  const end = Math.min(utcDay(endA), utcDay(endB))
  return Math.max(0, end - start + 1)
}

function statusOn(today: string, detail: DetailRecord): DetailStatus {
  if (today < detail.startDate) return 'upcoming'
  if (today > detail.endDate) return 'ended'
  return 'active'
}

export function buildDetailedOutRows(
  details: DetailRecord[],
  range: { from: string; to: string; today: string },
  supervisors: Map<string, string>,
): DetailedOutRow[] {
  return details.flatMap((detail) => {
    const days = overlapDays(detail.startDate, detail.endDate, range.from, range.to)
    if (days === 0) return []
    return [
      {
        key: `${detail.employeeId}|${detail.startDate}`,
        employeeId: detail.employeeId,
        identifier: detail.identifier,
        employee: detail.employee,
        detailType: detail.detailType,
        team: detail.team,
        teamId: detail.teamId,
        supervisorId: supervisors.get(detail.teamId) ?? null,
        destination: detail.destination,
        startDate: detail.startDate,
        endDate: detail.endDate,
        status: statusOn(range.today, detail),
        daysInRange: days,
        effect: `-1 available for ${days} ${days === 1 ? 'day' : 'days'}`,
      },
    ]
  })
}

export function filterDetailedOutRows(rows: DetailedOutRow[], filters: DetailedOutFilters): DetailedOutRow[] {
  return rows.filter(
    (row) =>
      (!filters.teamId || row.teamId === filters.teamId) &&
      (!filters.supervisorId || row.supervisorId === filters.supervisorId) &&
      (!filters.status || row.status === filters.status) &&
      (!filters.search || matchesEmployee(filters.search, row.employee, row.identifier)),
  )
}

export type DetailedOutColumn = 'employee' | 'detailType' | 'team' | 'destination' | 'startDate' | 'endDate' | 'status' | 'daysInRange'

export function totalPersonDays(rows: DetailedOutRow[]): number {
  return rows.reduce((sum, row) => sum + row.daysInRange, 0)
}
